import React, { useCallback, useEffect, useState, useMemo } from 'react';

import { TableCell, TableContainer, TableFooter, TablePagination, TableRow } from '@material-ui/core';
import NamedAvatar from 'components/NamedAvatar/NamedAvatar';
import MeshTable from 'components/TableComponents/MeshTable';
import useLocalStorage from 'components/hooks/useLocalStorage';
import { useDispatch } from 'react-redux';
import { ActionIconContainer } from 'screens/analytics/styles';
import styled from 'styled-components';
import { StyledTooltip } from 'styles/common';

import { ANALYTICS_TEXTS } from 'constants/ProjectConstants';
import { sendAnalyticsEmail } from 'constants/helper';
import { EmailIcon } from 'constants/icons';
import { localStorageKeys } from 'constants/localStorageKeys';
import { GOAL_ANALYTICS_EVENTS, sendGoalAnalyticsEvents } from 'constants/mixpanelHelper';
import { routesForTracking } from 'constants/routesForTracking';
import theme from 'constants/theme';

import { getTabularData } from 'services/analytics/goal';

import { ErrorMessage, NoData, Loader } from '../../commonComponents';
import DISPLAY_COMPONENT_MAP, { DISPLAY_COMPONENT_META } from '../../tableCellComponents';
import usePagination from '../../usePagination';
import { useGoalAnalytics } from '../GoalAnalyticsContext';

const defaultRowsPerPage = 10;

const employeeStatsTableRowsPerPage = 'employeeStatsTableRowsPerPage';

export const defaultRowsPerPageForGoals = {
  [employeeStatsTableRowsPerPage]: defaultRowsPerPage,
};

/**
 * @deprecated 02-04-2025
 */
const EmployeeStatsTable = ({}) => {
  const dispatch = useDispatch();

  const [rowsPerPageLS, setRowsPerPageLS] = useLocalStorage(
    localStorageKeys.analyticsGoalTableFilters,
    defaultRowsPerPageForGoals
  );
  const { data, dataCount, rowsPerPageOptions, rowsPerPage, pageNumber, setPageNumber, setRowsPerPage, setData } =
    usePagination({
      rowsPerPageOptions: [10, 20, 30],
      defaultRowsPerPage: rowsPerPageLS[employeeStatsTableRowsPerPage],
      initialPage: 0,
    });

  const {
    state: {
      currentState: { selectedCohort, selectedGroupByKey, selectedSplitByKey, selectedTimeBoundPeriods },
    },
    setExpandedRowDetailsView,
    setNoData,
    setErrorData,
  } = useGoalAnalytics();

  const [loading, setLoading] = useState(true);
  const [headers, setHeaders] = useState(null);

  const [orderBy, setOrderBy] = useState({ columnId: null, orderType: 'ASC' });

  const fetchData = () => {
    setLoading(true);
    getTabularData({
      cohort: selectedCohort,
      groupByKeys: selectedGroupByKey,
      splitByKeys: selectedSplitByKey,
      timeBounds: selectedTimeBoundPeriods,
      orderBy,
    })
      .then((res) => {
        const { columns, values } = res;

        // map data to add info from employee Name column to aggregated metrics.
        // find employee name header.
        const { id: employeeColumnId } = columns.filter(
          (header) => header.uuidType === DISPLAY_COMPONENT_META.EMPLOYEE_NAME.id
        )?.[0];

        // find aggregated metrics columns
        const aggregatedMetricsColumns = columns.filter(
          (header) =>
            header.uuidType === DISPLAY_COMPONENT_META.AGGREGATED_METRICS.id ||
            header.uuidType === DISPLAY_COMPONENT_META.AGGREGATED_METRICS_BAR.id ||
            header.uuidType === DISPLAY_COMPONENT_META.DONUT_GRAPH.id
        );

        const mappedValues = values.map((vObj) => {
          const employeeObj = vObj[employeeColumnId];

          const mappedAggregatedMetricsColumnsObj = aggregatedMetricsColumns.reduce((accumulatorObj, column) => {
            const { id } = column;
            return {
              ...accumulatorObj,
              [id]: {
                ...vObj[id],
                summaryTitle: employeeObj?.value,
              },
            };
          }, vObj);
          const sendEmail = () => {
            sendAnalyticsEmail(vObj[employeeColumnId]?.employee?.email, 'GOALS_TABLE');
          };
          return {
            ...mappedAggregatedMetricsColumnsObj,
            [employeeColumnId]: {
              ...vObj[employeeColumnId],
              onDisplayNameClick: () => {
                sendGoalAnalyticsEvents({
                  eventName: GOAL_ANALYTICS_EVENTS.openedExpandedView,
                  page: {
                    pageName: routesForTracking.analyticsGoals.pageName,
                    flow: 'Activity',
                  },
                  groupBy: 'Individual',
                });
                setExpandedRowDetailsView({
                  headerComponent: (
                    <NamedAvatar imageUrl={employeeObj?.imageUrl} displayName={employeeObj?.value} tooltipTex="" />
                  ),
                  infoObj: { uuid: employeeObj?.uuid },
                });
              },
              actionButtonIcon: (
                <StyledTooltip title="Send email">
                  <ActionIconContainer onClick={sendEmail}>
                    <EmailIcon color={theme.palette.icons.blue} size="tiniest" />
                  </ActionIconContainer>
                </StyledTooltip>
              ),
            },
          };
        });

        console.log(columns, mappedValues);
        setHeaders(columns);
        setData(mappedValues);

        if (mappedValues.length === 0) {
          setNoData(true);
        }
      })
      .catch((err) => {
        console.log(err);
        setErrorData(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedCohort && selectedGroupByKey && selectedSplitByKey && selectedTimeBoundPeriods) {
      fetchData();
    }
  }, [selectedCohort, selectedGroupByKey, selectedSplitByKey, selectedTimeBoundPeriods]);

  useEffect(() => {
    if (orderBy.columnId !== null) fetchData();
  }, [orderBy]);

  const handleSort = useCallback(
    (id, orderBy) => {
      if (orderBy.columnId === id) {
        setOrderBy((prev) => ({
          ...prev,
          orderType: prev.orderType === 'ASC' ? 'DESC' : 'ASC',
        }));
      } else {
        setOrderBy({
          columnId: id,
          orderType: 'ASC',
        });
      }
    },
    [setOrderBy]
  );

  const mappedHeaders = useMemo(() => {
    if (!headers) return [];

    const getFlexJustifyFromCellAlign = (align) => {
      switch (align) {
        case 'left':
          return 'flex-start';
        case 'center':
          return 'center';
        case 'right':
          return 'flex-end';
        default:
          return 'center';
      }
    };

    return headers.map((headerObj, index) => {
      const { id, displayName: title, uuidType } = headerObj;
      // id, title, width, justify, isSortable, orderBy, handleSort

      const justify = getFlexJustifyFromCellAlign(DISPLAY_COMPONENT_META[uuidType]?.align);
      const width =
        DISPLAY_COMPONENT_META[uuidType]?.id === DISPLAY_COMPONENT_META.EMPLOYEE_NAME.id
          ? `calc(100% - ${headers.length - 1} * 16rem)`
          : `16rem`;

      return {
        id,
        title,
        fixedWidth: width,
        justify,
        isSortable: DISPLAY_COMPONENT_META[uuidType]?.isSortable,
        orderBy,
        handleSort,
      };
    });
  }, [headers, orderBy, handleSort]);

  const dataRows = useMemo(() => {
    if (!data) return [];

    return data.map((rowObj) => (
      <StyledTableRow>
        {headers.map(({ id, uuidType }, index) => (
          <StyledTableCell
            align={DISPLAY_COMPONENT_META[uuidType]?.align}
            borderLeft={
              uuidType !== DISPLAY_COMPONENT_META.EMPLOYEE_NAME.id &&
              uuidType !== DISPLAY_COMPONENT_META.EMPLOYEE_DEPARTMENT.id
            }
          >
            {DISPLAY_COMPONENT_MAP[uuidType]({ ...rowObj[id], isEmployeeStateTable: true })}
          </StyledTableCell>
        ))}
      </StyledTableRow>
    ));
  }, [data, headers]);

  const tableFooter = useMemo(
    () => (
      <TableFooter>
        <StyledTableRow>
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            colSpan={0}
            count={dataCount}
            rowsPerPage={rowsPerPage}
            page={pageNumber}
            SelectProps={{
              inputProps: { 'aria-label': 'rows per page' },
            }}
            onChangePage={(_, pageNum) => setPageNumber(pageNum)}
            onChangeRowsPerPage={({ target }) => {
              setRowsPerPageLS({ ...rowsPerPageLS, [employeeStatsTableRowsPerPage]: target.value });
              setRowsPerPage(target.value);
            }}
          />
        </StyledTableRow>
      </TableFooter>
    ),
    [rowsPerPageOptions, dataCount, pageNumber, setPageNumber, setRowsPerPage]
  );

  return (
    <>
      {loading ? (
        <Loader />
      ) : headers === null || data === null ? (
        <ErrorMessage errorText={ANALYTICS_TEXTS.REVIEW_TABLE.ERROR_MSG} />
      ) : data.length === 0 ? (
        <NoData infoText={ANALYTICS_TEXTS.REVIEW_TABLE.NO_DATA} />
      ) : (
        <StyledTableContainer>
          <MeshTable headers={mappedHeaders} dataRows={dataRows} tableFooter={tableFooter} />
        </StyledTableContainer>
      )}
    </>
  );
};

const StyledTableRow = styled(TableRow)`
  th,
  td {
    border-bottom: 1px solid ${(props) => props.theme.palette.borders.lightGrey};
  }
`;

const StyledTableCell = styled(TableCell)`
  position: relative;
  border-left: ${(props) => props.borderLeft && `1px solid ${props.theme.palette.borders.lightGrey}`};
  cursor: ${(props) => (props.isSortable ? 'pointer' : 'inherit')};
`;

const StyledTableContainer = styled(TableContainer)`
  min-width: 1000px;
`;

export default EmployeeStatsTable;
